import axios from "axios";
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setToLocalStorage
} from "./storage-utils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json"
  },
  timeout: 15000
});

apiClient.interceptors.request.use((config) => {
  const token = getFromLocalStorage("accessToken");
  const isAuthRoute = config.url?.includes("/auth/");

  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }

  if (token && !isAuthRoute) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let queuedRequests = [];

function flushQueue(error, token) {
  queuedRequests.forEach((entry) => {
    if (error) {
      entry.reject(error);
      return;
    }
    entry.resolve(token);
  });
  queuedRequests = [];
}

async function refreshAccessToken() {
  const refreshToken = getFromLocalStorage("refreshToken");
  if (!refreshToken) {
    throw new Error("Missing refresh token");
  }

  const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
    refreshToken
  });

  const accessToken = response.data?.data?.tokens?.accessToken;
  const nextRefreshToken = response.data?.data?.tokens?.refreshToken;

  if (!accessToken) {
    throw new Error("Refresh token response missing access token");
  }

  setToLocalStorage("accessToken", accessToken);
  if (nextRefreshToken) {
    setToLocalStorage("refreshToken", nextRefreshToken);
  }

  return accessToken;
}

function logoutRedirect() {
  removeFromLocalStorage("accessToken");
  removeFromLocalStorage("refreshToken");
  removeFromLocalStorage("smcrc-auth-storage");
  window.location.href = "/auth/login";
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthRequest = originalRequest?.url?.includes("/auth/");
    const status = error.response?.status;

    if (!status || isAuthRequest || (status !== 401 && status !== 403)) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      logoutRedirect();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        queuedRequests.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const accessToken = await refreshAccessToken();
      flushQueue(null, accessToken);
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      flushQueue(refreshError, null);
      logoutRedirect();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export async function requestHandler(url, options = {}) {
  try {
    const { method = "GET", body, headers, params } = options;
    const response = await apiClient({
      url,
      method,
      headers,
      params,
      data: body
    });
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      return error.response.data;
    }

    return {
      success: false,
      message: error.message || "Request failed"
    };
  }
}

export default apiClient;
